import { useCallback, useEffect, useState } from 'react';
import { CenterState, ErrorBanner, InfoBanner, Screen, Spinner } from '../components/Ui';
import { ApiError, api } from '../lib/api';
import { formatDate, formatPercent, plural } from '../lib/format';
import {
  MEMBER_STATUS_LABELS,
  STATS_WINDOW_LABELS,
  type AudienceStat,
  type DayStat,
  type StatsResponse,
  type StatsWindow,
} from '../types';

type Props = { collectionId: string };

const WINDOWS: StatsWindow[] = ['day', '3days', 'week', 'all'];

export function StatsView({ collectionId }: Props) {
  const [period, setPeriod] = useState<StatsWindow>('all');
  const [data, setData] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (next: StatsWindow) => {
      setError(null);
      try {
        setData(await api.collectionStats(collectionId, next));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить статистику');
      }
    },
    [collectionId],
  );

  useEffect(() => {
    void load(period);
  }, [load, period]);

  if (error) {
    return (
      <Screen>
        <ErrorBanner onRetry={() => void load(period)}>{error}</ErrorBanner>
      </Screen>
    );
  }

  if (!data) {
    return (
      <Screen>
        <CenterState>
          <Spinner />
        </CenterState>
      </Screen>
    );
  }

  const { stats, collection, viewsSince } = data;
  const empty = stats.views.total === 0 && stats.clicks.total === 0;

  // Пост опубликован раньше, чем завели счётчик открытий. Переходы у него
  // есть, открытий нет и не появится — без этой оговорки цифры выглядят
  // поломкой, а CTR посчитать не от чего.
  const olderThanCounter =
    collection.publishedAt !== null &&
    (viewsSince === null || collection.publishedAt < viewsSince);

  return (
    <Screen>
      <header className="home-header">
        <h1>Статистика</h1>
        <p className="muted small">
          {collection.captionPreview?.trim() || 'Без текста'}
        </p>
        <p className="muted small">
          {collection.publishedAt
            ? `опубликована ${formatDate(collection.publishedAt)}`
            : 'ещё не опубликована'}
        </p>
      </header>

      <div className="window-switch">
        {WINDOWS.map((item) => (
          <button
            key={item}
            className={`window-switch__item ${
              item === period ? 'window-switch__item--active' : ''
            }`}
            onClick={() => setPeriod(item)}
          >
            {STATS_WINDOW_LABELS[item]}
          </button>
        ))}
      </div>
      <p className="muted small">
        Период считается от публикации поста, а не от сегодняшнего дня.
      </p>

      {olderThanCounter && (
        <InfoBanner>
          Открытия считаются {viewsSince ? `с ${formatDate(viewsSince)}` : 'только у новых постов'}.
          У этого поста их нет — он опубликован раньше. Переходы посчитаны все.
        </InfoBanner>
      )}

      {stats.truncated && (
        <InfoBanner>
          Событий слишком много, показана только часть — цифры занижены.
        </InfoBanner>
      )}

      {empty ? (
        <CenterState>
          <p className="muted">
            За этот период ничего не было. Попробуй период пошире.
          </p>
        </CenterState>
      ) : (
        <>
          <div className="stat-tiles">
            <Tile value={stats.views.total} caption="открытий" />
            <Tile value={stats.clicks.total} caption="переходов" />
            <Tile
              value={stats.ctr === null ? '—' : formatPercent(stats.ctr)}
              caption="переходов на открытие"
            />
          </div>

          <p className="muted small">
            {stats.views.unique > 0 ? (
              <>
                разных людей: {stats.views.unique} · дошли до перехода:{' '}
                {formatPercent(stats.conversion)}
              </>
            ) : (
              <>разных людей с переходом: {stats.clicks.unique}</>
            )}
          </p>

          {stats.views.total > 0 && (
            <Audience rows={stats.audience} total={stats.views.total} />
          )}

          {stats.daily.length >= 2 && <Daily daily={stats.daily} />}

          <section className="block">
            <h2>По карточкам</h2>
            {stats.clicks.total === 0 ? (
              <p className="muted small">Переходов за этот период не было.</p>
            ) : (
              <div className="card-stats">
                {stats.cards.map((card) => (
                  <div key={card.id} className="card-stat">
                    <div className="card-stat__head">
                      <span className="card-stat__title">{card.title}</span>
                      <span className="card-stat__count">{card.clicks}</span>
                    </div>
                    <div className="share-bar">
                      <div
                        className="share-bar__fill"
                        style={{ width: `${Math.round(card.share * 100)}%` }}
                      />
                    </div>
                    {card.brand && <span className="muted small">{card.brand}</span>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </Screen>
  );
}

function Tile({ value, caption }: { value: number | string; caption: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-tile__value">{value}</span>
      <span className="stat-tile__caption">{caption}</span>
    </div>
  );
}

/**
 * Кто открывал: подписчики канала или люди со стороны.
 *
 * Отвечает на вопрос, ради которого пост и делается: он работает на твою
 * аудиторию или его унесли репостами. Статус берётся на момент открытия —
 * подписавшийся после того, как увидел подборку, попадает к подписчикам.
 */
function Audience({ rows, total }: { rows: AudienceStat[]; total: number }) {
  const known = rows.filter((row) => row.status !== 'unknown' && row.views > 0);

  // Спросить не удалось вовсе — тогда честнее объяснить, почему пусто,
  // чем рисовать таблицу из одних прочерков.
  if (known.length === 0) {
    return (
      <section className="block">
        <h2>Кто открывал</h2>
        <p className="muted small">
          Про этот пост подписку не спрашивали. Она проверяется только у
          опубликованных подборок и только пока бот остаётся администратором
          канала.
        </p>
      </section>
    );
  }

  const unknown = rows.find((row) => row.status === 'unknown');

  return (
    <section className="block">
      <h2>Кто открывал</h2>
      <div className="audience">
        {rows
          // Строка «неизвестно» появляется, только если такие открытия есть.
          // Нулевые «подписчики» и «не подписаны», наоборот, остаются: ноль
          // напротив сегмента — это ответ, а пропавшая строка читается сбоем.
          .filter((row) => row.status !== 'unknown' || row.views > 0)
          .map((row) => (
            <div
              key={row.status}
              className={`audience__row ${
                row.status === 'unknown' ? 'audience__row--unknown' : ''
              }`}
            >
              <div className="audience__head">
                <span>{MEMBER_STATUS_LABELS[row.status]}</span>
                <span className="audience__count">
                  {row.views} · {formatPercent(row.views / total)}
                </span>
              </div>
              <div className="share-bar">
                <div
                  className="share-bar__fill"
                  style={{ width: `${Math.round((row.views / total) * 100)}%` }}
                />
              </div>
              <span className="muted small">
                {row.people} {plural(row.people, ['человек', 'человека', 'человек'])} · дошли
                до перехода: {formatPercent(row.conversion)}
              </span>
            </div>
          ))}
      </div>
      {unknown && unknown.views > 0 && (
        <p className="muted small">
          «Неизвестно» — не «не подписан»: у этих открытий не получилось
          спросить Telegram. Так бывает, если подборку открыли до публикации
          или бота на время убрали из администраторов канала.
        </p>
      )}
    </section>
  );
}

/**
 * Затухание поста по суткам. Столбики масштабируются к общему максимуму,
 * иначе день с двумя открытиями выглядел бы так же, как день с двумя сотнями.
 */
function Daily({ daily }: { daily: DayStat[] }) {
  const max = Math.max(...daily.map((d) => Math.max(d.views, d.clicks)), 1);
  const height = (value: number) => `${Math.max(value === 0 ? 0 : 4, (value / max) * 100)}%`;

  return (
    <section className="block">
      <h2>По суткам</h2>
      <div className="daily">
        {daily.map((day) => (
          <div key={day.day} className="daily__col">
            <div className="daily__bars">
              <span
                className="daily__bar daily__bar--views"
                style={{ height: height(day.views) }}
                title={`${day.views} ${plural(day.views, ['открытие', 'открытия', 'открытий'])}`}
              />
              <span
                className="daily__bar daily__bar--clicks"
                style={{ height: height(day.clicks) }}
                title={`${day.clicks} ${plural(day.clicks, ['переход', 'перехода', 'переходов'])}`}
              />
            </div>
            <span className="daily__day">{day.day + 1}</span>
          </div>
        ))}
      </div>
      <p className="muted small">
        Светлое — открытия, тёмное — переходы. Цифра — сутки от публикации.
      </p>
    </section>
  );
}
